import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NAV_ITEMS } from './navigation';
import { can } from '@/lib/permissions';
import { useUnreadMessages } from '@/features/messages/useUnread';
import type { Role } from '@/types/domain';
import { cn } from '@/lib/cn';
import { MobileActions } from '@/components/system/QuickDock';

/** Mobile bottom navigation — impeccable mobile view (5 primary modules +
 *  «Acciones»: subir, acción rápida y Loruel sin botones flotantes). */
export function MobileNav({ role }: { role: Role }) {
  const { t } = useTranslation();
  const items = NAV_ITEMS.filter((i) => !i.capability || can(role, i.capability)).slice(0, 5);
  const showsMessages = items.some((i) => i.to === '/messages');
  const unread = useUnreadMessages(showsMessages && can(role, 'messages.use'));

  return (
    // 2-nov: el margen inferior respeta la barra de inicio del iPhone (safe area).
    <nav className="fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)] lg:hidden">
      <div className="glass-strong mx-3 mb-3 flex items-center justify-around rounded-2xl px-2 py-2">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-[10px] transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <span className="relative">
              <item.icon className="h-5 w-5" />
              {item.to === '/messages' && unread > 0 && (
                <span
                  className="absolute -right-1.5 -top-1 grid h-3.5 min-w-3.5 place-items-center rounded-full bg-primary px-0.5 text-[9px] font-bold leading-none text-white"
                  aria-label={t('messages.unreadAria', { count: unread })}
                >
                  {unread}
                </span>
              )}
            </span>
            <span className="truncate">{t(item.labelKey)}</span>
          </NavLink>
        ))}
        <MobileActions />
      </div>
    </nav>
  );
}
